import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Activity, ItineraryDay, Meal } from '../../../types';
import TimePicker from '../../ui/TimePicker';
import { Route, X, Icon } from '../../ui/Icon';

export interface MealStopActions {
  days: ItineraryDay[];
  onLink: (meal: Meal, activityId: string | null) => Promise<void>;
  onCreate: (meal: Meal, startTime: string | null) => Promise<void>;
}

interface Props {
  meal: Meal;
  stops: MealStopActions;
}

/**
 * The stops of a day a meal may be linked to. Booking cards are rebuilt from
 * their booking and the origin card mirrors another day, so neither can carry
 * a synced title; a stop that already has a meal has its one.
 */
export function linkableStops(days: ItineraryDay[], dayId: string): Activity[] {
  return (days.find(d => d.id === dayId)?.activities ?? []).filter(a =>
    !a.isOrigin && !a.hotelId && !a.flightId && !a.bookingId && !a.mealId);
}

export const stopLabel = (a: Activity) => (a.startTime ? `${a.startTime} · ${a.title}` : a.title);

/**
 * The meal as a stop of its day: linked to one that is already in the plan,
 * or made into a new one.
 *
 * ADR-0024 — once linked, the title and the place are the same on both sides,
 * whichever is edited. The hour is the stop's; slot and ingredients stay here.
 */
export default function MealStopLink({ meal, stops }: Props) {
  const { t } = useTranslation();
  const [startTime, setStartTime] = useState('');
  const [busy, setBusy] = useState(false);

  const linkable = useMemo(() => linkableStops(stops.days, meal.dayId), [stops.days, meal.dayId]);

  async function run(action: Promise<void>) {
    setBusy(true);
    try { await action; } finally { setBusy(false); }
  }

  const linked = meal.activity;
  if (linked) {
    const stopDay = linked.dayId === meal.dayId ? null : stops.days.find(d => d.id === linked.dayId);
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-teal-700 dark:text-teal-300">
        <Icon as={Route} size={12} className="shrink-0" />
        <span className="flex-1 min-w-0 truncate">
          {stopDay
            ? t('meals.stop.otherDay', { day: stopDay.dayNumber })
            : linked.startTime
              ? t('meals.stop.linkedAt', { time: linked.startTime })
              : t('meals.stop.linked')}
        </span>
        <button
          onClick={() => run(stops.onLink(meal, null))}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer disabled:opacity-50"
        >
          <Icon as={X} size={10} />
          {t('meals.stop.unlink')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-col sm:flex-row gap-1.5">
        {linkable.length > 0 && (
          <select
            value=""
            disabled={busy}
            onChange={e => { if (e.target.value) void run(stops.onLink(meal, e.target.value)); }}
            aria-label={t('meals.stop.pick')}
            className="input sm:!w-44 shrink-0 !text-xs !px-2 !py-1.5 cursor-pointer"
          >
            <option value="">{t('meals.stop.pick')}</option>
            {linkable.map(a => <option key={a.id} value={a.id}>{stopLabel(a)}</option>)}
          </select>
        )}
        <div className="flex items-stretch gap-1.5">
          <StopTime value={startTime} onChange={setStartTime} />
          <button
            onClick={() => run(stops.onCreate(meal, startTime || null))}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 dark:text-teal-300 dark:bg-teal-900/25 dark:hover:bg-teal-900/40 transition-colors cursor-pointer disabled:opacity-50"
          >
            <Icon as={Route} size={12} />
            {t('meals.stop.create')}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 dark:text-slate-500">{t('meals.stop.hint')}</p>
    </div>
  );
}

/** The optional hour of the new stop. TimePicker keeps its own padding; only the width is ours. */
export function StopTime({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="w-28 shrink-0" aria-label={t('meals.stop.timePlaceholder')}>
      <TimePicker value={value} onChange={onChange} />
    </div>
  );
}
